/**
 * Analyzes SERP data to extract various SEO metrics.
 * 
 * @param {Object} data - The raw JSON data from DataForSEO
 * @returns {Object} Structured analysis object
 */
export const analyzeData = (data) => {
    if (!data) return null;

    const items = data.result[0].items;
    const keyword = data.result[0].keyword;

    // Count result types
    const typeCount = {};
    items.forEach(item => {
        const type = item.type;
        typeCount[type] = (typeCount[type] || 0) + 1;
    });

    const resultTypes = Object.entries(typeCount).map(([name, value]) => ({
        name: name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value
    }));

    // Extract price data
    const priceData = [];
    items.forEach(item => {
        if (item.type === 'popular_products' && item.items) {
            item.items.forEach(product => {
                if (product.price && product.price.current) {
                    priceData.push({
                        name: product.title.substring(0, 30) + '...',
                        price: product.price.current,
                        seller: product.seller,
                        currency: product.price.currency
                    });
                }
            });
        }
        if (item.price && item.price.current) {
            priceData.push({
                name: item.title.substring(0, 30) + '...',
                price: item.price.current,
                seller: item.breadcrumb || item.domain,
                currency: item.price.currency
            });
        }
    });

    // Competitor Analysis
    const domainCount = {};
    const organicItems = [];

    items.forEach((item, index) => {
        if (item.type === 'organic' && item.domain) {
            domainCount[item.domain] = (domainCount[item.domain] || 0) + 1;
            organicItems.push({
                domain: item.domain,
                position: item.rank_absolute || index + 1,
                rankGroup: item.rank_group,
                title: item.title,
                url: item.url
            });
        }
    });

    const topDomains = Object.entries(domainCount)
        .map(([domain, count]) => ({ domain, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

    // SERP Difficulty Analysis
    const nonOrganicFeatures = items.filter(i => i.type !== 'organic').map(i => i.type);
    const uniqueFeatures = [...new Set(nonOrganicFeatures)];
    const organicCount = items.filter(i => i.type === 'organic').length;
    const firstOrganicPosition = items.findIndex(i => i.type === 'organic') + 1;

    let difficultyScore = 0;
    if (uniqueFeatures.includes('popular_products')) difficultyScore += 2;
    if (uniqueFeatures.includes('people_also_ask')) difficultyScore += 1;
    if (uniqueFeatures.includes('video')) difficultyScore += 1;
    if (uniqueFeatures.includes('images')) difficultyScore += 0.5;
    if (uniqueFeatures.includes('compare_sites')) difficultyScore += 1.5;
    if (firstOrganicPosition > 3) difficultyScore += 2;
    if (organicCount < 8) difficultyScore += 1;
    difficultyScore = Math.min(10, Math.round(difficultyScore));

    const serpDifficulty = {
        score: difficultyScore,
        features: uniqueFeatures.map(f => f.replace(/_/g, ' ')),
        organicPercentage: Math.round((organicCount / items.length) * 100),
        firstOrganicPosition,
        isCommercial: priceData.length > 0 || uniqueFeatures.includes('popular_products')
    };

    // Detailed Competitor Analysis
    const organicDomains = organicItems.map(item => {
        let inShopping = false;
        items.forEach(i => {
            if (i.type === 'popular_products' && i.items) {
                i.items.forEach(prod => {
                    if (prod.seller && prod.seller.toLowerCase().includes(item.domain.toLowerCase())) {
                        inShopping = true;
                    }
                });
            }
        });

        return {
            ...item,
            multiPresence: inShopping,
            inShopping
        };
    });

    // Content Opportunities
    const paaQuestions = [];
    items.forEach(item => {
        if (item.type === 'people_also_ask' && item.items) {
            paaQuestions.push(...item.items.map(q => q.title));
        }
    });

    const organicTitles = organicItems.slice(0, 5).map(i => i.title.toLowerCase());
    const paaNotCovered = paaQuestions.filter(paa => {
        const paaLower = paa.toLowerCase();
        return !organicTitles.some(title =>
            title.includes(paaLower.substring(0, 20)) ||
            paaLower.includes(title.substring(0, 20))
        );
    });

    const relatedSearches = [];
    items.forEach(item => {
        if (item.type === 'people_also_search' && item.items) {
            relatedSearches.push(...item.items);
        }
    });

    const contentOpportunities = {
        paaNotCovered: paaNotCovered.slice(0, 5),
        refinementGaps: data.result[0].refinement_chips?.items?.map(chip => chip.title) || [],
        relatedSearchGaps: relatedSearches.filter(rs => {
            const rsLower = rs.toLowerCase();
            return !organicTitles.some(title => title.includes(rsLower.substring(0, 15)));
        }).slice(0, 5),
        missingFormats: {
            video: !uniqueFeatures.includes('video'),
            images: !uniqueFeatures.includes('images')
        }
    };

    // AI Overview (SGE) Extraction
    const aiOverviewItem = items.find(item => item.type === 'ai_overview');
    const aiOverview = aiOverviewItem ? {
        present: true,
        text: aiOverviewItem.text || "Contenuto generativo rilevato.",
        references: aiOverviewItem.references || [] // citations
    } : { present: false, text: null, references: [] };

    return {
        keyword,
        resultTypes,
        priceData,
        topDomains,
        relatedSearches,
        refinementChips: data.result[0].refinement_chips?.items?.map(chip => chip.title) || [],
        paaQuestions,
        totalResults: items.length,
        organicCount,
        serpDifficulty,
        organicDomains,
        contentOpportunities,
        aiOverview
    };
};

/**
 * Constants for UI
 */
export const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#ef4444', '#06b6d4', '#84cc16', '#f97316', '#14b8a6'];

/**
 * Helper to process text and identify clickable parts for the UI
 * Returns an array of parts that can be mapped to spans/buttons
 */
export const processClickableText = (text) => {
    if (!text) return [{ text: '', isLink: false }];

    const patterns = [
        { regex: /(\d+)\s*domand[ei]\s*PAA/gi, tab: 'opportunities' },
        { regex: /(\d+)\s*PAA/gi, tab: 'related' },
        { regex: /(\d+)\s*competitor/gi, tab: 'competitors' },
        { regex: /(\d+)\s*domini/gi, tab: 'competitors' },
        { regex: /(\d+)\s*ricerche\s*correlate/gi, tab: 'related' },
        { regex: /(\d+)\s*filtri/gi, tab: 'opportunities' },
        { regex: /difficulty\s*score/gi, tab: 'difficulty' },
        { regex: /SERP\s*features/gi, tab: 'difficulty' },
    ];

    let parts = [{ text, isLink: false }];

    patterns.forEach(({ regex, tab }) => {
        const newParts = [];
        parts.forEach(part => {
            if (part.isLink) {
                newParts.push(part);
                return;
            }

            const matches = [...part.text.matchAll(regex)];
            if (matches.length === 0) {
                newParts.push(part);
                return;
            }

            let lastIndex = 0;
            matches.forEach(match => {
                if (match.index > lastIndex) {
                    newParts.push({ text: part.text.slice(lastIndex, match.index), isLink: false });
                }
                newParts.push({ text: match[0], isLink: true, tab });
                lastIndex = match.index + match[0].length;
            });

            if (lastIndex < part.text.length) {
                newParts.push({ text: part.text.slice(lastIndex), isLink: false });
            }
        });
        parts = newParts;
    });

    return parts;
};
